// Zentrale Scroll-Policy fuer F-01 (Scroll-Verhalten verkehrt herum).
//
// Regel des Nutzers (maßgeblich):
//  - AUSWAHL-/AKTIONS-Routen (wo man etwas TUT: Wizard-Schritte, Kategorie-
//    Auswahl, Erzaehler-/Welten-Baum-Auswahl) → nach dem Wechsel IMMER ganz oben.
//  - UEBERSICHTS-Routen (Listen: Abenteuer-/Charakter-Uebersicht usw.) → beim
//    Zurueckkehren die zuletzt gemerkte Scroll-Position wiederherstellen.
//
// Gescrollt wird IMMER das Fenster/Dokument (window.scrollTo /
// document.documentElement.scrollTop) — es gibt keinen internen Scroll-Container
// (AppLayout .main + PageContainer .container haben kein overflow).
//
// Chat-Routen laufen ausserhalb des AppLayout (eigene ChatShell, 100dvh) und
// werden von dieser Policy NICHT beruehrt, weil der Hook nur in AppLayout laeuft.
//
// Korrektur C-01 (Politur F-01):
//  1) Overview->Overview-Navigation korrumpierte die gemerkte Scroll-Position
//     der verlassenen Seite. Ursache (React-Commit-Reihenfolge): der
//     useLayoutEffect-RESTORE der NEUEN Route lief VOR dem Cleanup des
//     passiven scroll-Listeners der ALTEN Route; das Cleanup rief
//     overviewPositions.set(alteRoute, readScrollTop()) auf — readScrollTop()
//     lieferte aber den soeben fuer die NEUE Route restaurierten Wert, weil
//     window.scrollTo synchron wirkt → verlassene Position unter falschem
//     Key ueberschrieben (z.B. /dashboard mit 500 verlassen, Rueckkehr landet
//     bei 0 statt 500, bricht BottomNav-Tab-Wechsel-Kern-Anforderung).
//     Fix: das re-lesende set im Cleanup ENTFERNEN. Der passive Listener
//     haelt die Position bereits kontinuierlich korrekt unter dem Key der
//     aktiven Uebersichts-Route; der letzte echte Scroll-Event der alten
//     Route steht damit schon richtig in der Map.
//  2) Restore-Robustheit bei async geladenen Listen (React Query): das
//     synchrone scrollTo im useLayoutEffect kann auf eine noch zu niedrige
//     Dokumenthoehe (Skeletons) treffen → Browser clampt → Seite waechst
//     spaeter, Position bleibt zu niedrig. Fix: nach dem ersten Setzen die
//     Position einige Frames per requestAnimationFrame erneut anwenden, bis
//     die Zielposition erreichbar ist oder ein Frame-Budget aufgebraucht ist;
//     bei jeder Navigation und beim Unmount per cancelAnimationFrame sauber
//     abbrechen. NUR fuer den Overview-Restore-Pfad, Action->top bleibt
//     unveraendert (synchron, kein rAF).

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { PaneId } from '../layout/paneScroll';

/**
 * AKTIONS-Routen: nach jedem Wechsel auf eine solche Route IMMER nach oben.
 * Pfad-Praefixe (startsWith), damit Editor-Routen mit IDs (z.B.
 * /characters/123/edit) ebenfalls greifen.
 */
const ACTION_PATH_PREFIXES = [
  '/campaign-new',
  '/world-tree',
  '/spielbau',
  '/table-rounds/new',
  '/characters/new',
  '/world-element-edit',
] as const;

/**
 * Editor-Routen mit ID: /characters/:id/edit ist Aktion, /characters (Liste)
 * dagegen Uebersicht. Per Regex sauber von der Liste getrennt.
 */
const ACTION_PATH_PATTERNS: RegExp[] = [/^\/characters\/[^/]+\/edit\/?$/];

/**
 * Klassifiziert eine Location als 'action' oder 'overview'.
 * Sonderfall /world: MIT ?type= (Kategorie) oder ?area= (Bereich) = Aktion, OHNE = Uebersicht.
 * (Same-Route-Aktionswechsel innerhalb /world werden zusaetzlich in der Seite
 * selbst per window.scrollTo(0,0) behandelt — siehe world-elements/index.tsx.)
 *
 * `paneId` (Paket K, docs/30 §8.5): eine pane-bezogene Aktions-Location setzt
 * NUR ihren eigenen Pane auf 0, nicht das ganze Fenster. Der Default 'window'
 * ist das heutige Verhalten — bestehende Aufrufer bleiben unveraendert. (Der
 * Parameter wird hier bewusst noch nicht ausgewertet: die Klassifikation ist
 * pane-unabhaengig; die pane-scoping-Logik lebt in der Split-Ansicht, die den
 * Parameter mitfuehrt, damit z.B. 'list' Detail-Aktionen ignorieren kann.)
 */
export function isActionLocation(pathname: string, search: string, paneId: PaneId = 'window'): boolean {
  void paneId;
  if (ACTION_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return true;
  }
  if (ACTION_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return true;
  }
  if (pathname === '/world') {
    // ?type= (Kategorie) und ?area= (Bereich der Welt-Uebersicht) waehlen etwas
    // aus → Aktion. Ohne beides ist es die Uebersicht mit gemerkter Position.
    const params = new URLSearchParams(search);
    return params.has('type') || params.has('area');
  }
  return false;
}

function readScrollTop(): number {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function scrollWindowTo(top: number): void {
  // Beide Achsen setzen; 'auto' (kein Smooth) fuer sofortiges Springen.
  window.scrollTo({ top, left: 0, behavior: 'auto' });
}

const overviewPositions = new Map<string, number>();

export function resetScrollPositionsForTests(): void {
  overviewPositions.clear();
}

/**
 * Installiert die Scroll-Policy. Gehoert in AppLayout (laeuft genau einmal pro
 * App-Shell-Instanz). Setzt history.scrollRestoration NICHT — das macht App.tsx
 * einmalig global auf 'manual'.
 */
export function useScrollPolicy(): void {
  const location = useLocation();
  // Pfad+Suche der vorherigen Location, um VOR dem Wechsel die Position der
  // verlassenen Uebersicht zu sichern.
  const previousKey = useRef<string | null>(null);
  // rAF-Handle(s) fuer den robusten Overview-Restore (C-01.2). Es steht hoechstens
  // der jeweils naechste, noch nicht gefeuerte Handle drin — bereits gefeuerte
  // Handles werden beim Re-Schedule ueberschrieben. Werden bei jeder Navigation
  // (Layout-Effect) und beim Unmount der App-Shell per cancelAnimationFrame
  // sauber abgebrochen.
  const restoreFrameHandle = useRef<number | null>(null);
  // Generation-Counter: verhindert, dass eine bereits gestartete Restore-Schleife
  // nach cancel/startRobustRestore weiter Frames anstoesst, falls der Browser den
  // aktuellen rAF-Callback nicht mehr abbrechen kann.
  const restoreGeneration = useRef(0);

  function cancelPendingRestore(): void {
    // Generation erhoehen → laufende/ anstehende apply-Callbacks erkennen, dass
    // sie stale sind, und kehren ohne Re-Schedule zurueck.
    restoreGeneration.current += 1;
    if (restoreFrameHandle.current !== null) {
      cancelAnimationFrame(restoreFrameHandle.current);
      restoreFrameHandle.current = null;
    }
  }

  /**
   * rAF-gestuetzter Overview-Restore (C-01.2): setzt targetTop nach dem
   * ersten Layout-Pass in den naechsten Frames erneut, damit spaet
   * wachsende Dokumenthoehen (React-Query-Listen, Skeletons) das
   * Scroll-Ziel nicht ueberholen. Bricht ab, sobald die Zielposition
   * erreicht ist oder das Frame-Budget aufgebraucht ist.
   */
  function startRobustRestore(targetTop: number): void {
    // Vorherige, noch laufende Restore-Schleife abbrechen — greift bei
    // aufeinanderfolgenden Navigationen in schneller Folge.
    cancelPendingRestore();
    const myGeneration = restoreGeneration.current;
    const FRAMES = 5; // ~83ms bei 60Hz — reicht fuer typische Skeleton->Inhalt-Wechsel
    let attempt = 0;
    const apply = () => {
      // Stale? (Navigation ist dazwischengekommen oder App-Shell wurde abgebaut.)
      if (myGeneration !== restoreGeneration.current) {
        restoreFrameHandle.current = null;
        return;
      }
      attempt += 1;
      // Nur erneut scrollen, wenn die Zielposition noch nicht hinreichend genau
      // erreicht ist — sonst wuerde jeder weitere Frame den passiven Listener
      // veranlassen, den (clamp-)Wert unter dem Pfad-Key neu zu schreiben.
      if (Math.abs(readScrollTop() - targetTop) > 1) {
        scrollWindowTo(targetTop);
      }
      if (attempt < FRAMES && Math.abs(readScrollTop() - targetTop) > 1) {
        restoreFrameHandle.current = requestAnimationFrame(apply);
      } else {
        // Erreicht oder Budget aufgebraucht.
        restoreFrameHandle.current = null;
      }
    };
    restoreFrameHandle.current = requestAnimationFrame(apply);
  }

  // Position der aktuell sichtbaren Uebersicht laufend mitschreiben, damit auch
  // ein cross-route-Wechsel ohne sauberes "Verlassen-Event" die letzte Position
  // kennt. Nur fuer Uebersichts-Routen aktiv.
  //
  // KORREKTUR C-01.1: Beim Verlassen KEIN erneutes readScrollTop() in den
  // Cleanup schreiben! Sonst wuerde der Cleanup NACH dem Layout-Effect der
  // NEUEN Route laufen und mit readScrollTop() den Wert zuruecklesen, den
  // window.scrollTo soeben fuer die NEUE Route gesetzt hat — unter dem
  // Key der ALTEN Route abgelegt → verlassene Position korrumpiert
  // (z.B. /dashboard mit remembered 500 verlassen, Rueckkehr landet bei 0
  // statt 500, bricht die BottomNav-Tab-Wechsel-Kern-Anforderung).
  // Statt dessen vertrauen wir auf den laufenden Listener: er hat waehrend
  // der aktiven Zeit unter dem aktuellen Pfad-Key kontinuierlich die echte
  // Live-Position mitgeschrieben. Der letzte echte Scroll-Event der alten
  // Route steht also bereits korrekt in der Map.
  useEffect(() => {
    const isAction = isActionLocation(location.pathname, location.search);
    if (isAction) return undefined;

    const key = location.pathname;
    const handleScroll = () => {
      overviewPositions.set(key, readScrollTop());
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      // Beim Verlassen KEIN erneutes Setzen — siehe Kommentar oben (C-01.1).
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, location.search]);

  // Scroll-Entscheidung VOR dem Paint (useLayoutEffect verhindert sichtbares
  // Flackern beim Wiederherstellen der Position).
  useLayoutEffect(() => {
    const currentKey = `${location.pathname}${location.search}`;
    // Kein Re-Run bei reinem State-Wechsel: nur reagieren, wenn sich die
    // Location wirklich geaendert hat.
    if (previousKey.current === currentKey) return;
    previousKey.current = currentKey;

    // Jede Navigation bricht eine eventuell noch laufende Restore-Schleife
    // der Vorgaenger-Route ab (C-01.2).
    cancelPendingRestore();

    if (isActionLocation(location.pathname, location.search)) {
      // Aktions-/Auswahl-Route → immer ganz oben. Synchron, KEIN rAF-Restore
      // (Action->top-Verhalten darf nicht durch async-Listen-Wachstum
      // zaehlen oder auf einen spaeteren Frame warten).
      scrollWindowTo(0);
      return;
    }

    // Uebersichts-Route → zuletzt gemerkte Position wiederherstellen (oder 0).
    // Synchron zuerst (damit der erste Frame schon an der richtigen Stelle
    // sitzt, sichtbares Flackern wird vermieden), danach per rAF-Schleife
    // nachjustieren fuer Faelle, in denen React-Query-Listen erst NACH dem
    // Layout-Pass die Dokumenthoehe wachsen lassen — sonst bleibt das
    // Scroll-Ziel an der zu kleinen Skeleton-Hoehe "kleben" (C-01.2).
    const remembered = overviewPositions.get(location.pathname) ?? 0;
    scrollWindowTo(remembered);
    if (remembered > 0) {
      startRobustRestore(remembered);
    }
    // startRobustRestore ist eine absichtlich stabile Funktions-Referenz
    // (innerhalb des Hooks definiert, nur ueber Refs/cancels an Aussenwelt
    // gekoppelt) — soll NUR bei Location-Wechsel feuern, nicht bei jedem
    // Render, in dem die Identitaet neu waere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Beim Unmount der App-Shell alle offenen rAF-Restores abbrechen (C-01.2).
  useEffect(() => {
    return () => {
      cancelPendingRestore();
    };
  }, []);
}
